import { useEffect, useMemo, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { getProfileCommonJss } from '../../components/client/profile/ProfileCommonJss'
import CurrentClientContext from '../../providers/CurrentClientProvider'
import ClientProfileLocationList from '../../components/client/profile/ClientProfileLocationList'
import ClientProfileClassList from '../../components/client/profile/ClientProfileClassList'
import ClientLocationClassLinkEdit from '../../components/client/profile/ClientLocationClassLinkEdit'
import ClassBulkEdit from '../../components/client/profile/ClassBulkEdit'
import ClientService from '../../services/ClientService'
import TipsContext from '../../providers/GlobalTip'
import ClientClassEdit from '../../components/client/profile/ClientClassEdit'
import ClientClassCreate from '../../components/client/class/ClientClassCreate'
import LabelService from '../../services/LabelService'
import AuthProvider from '../../providers/AuthProvider'
import _ from 'underscore'

type Props = {
  visualVariant?: string
}

type CurrentClientContextValue = {
  client?: {
    Id: number
    ClientCode?: string
  } | null
}

type AuthContextValue = {
  checkPermission?: (permission: string) => boolean
}

type ClientClass = {
  id: number
  client_id?: number
  name?: string
  code?: string
  parent_class_id?: number | null
  deleted_at?: string | null
  isActive?: number | boolean
  location_links?: Array<Record<string, any>>
  _type?: string
  [key: string]: any
}

type ClientLocationClass = {
  locationclassid: number
  clientclass_id: number
  [key: string]: any
}

type ClientLocation = {
  id: number
  client_location_id?: number
  client_id: number
  drop_date?: string | null
  class_links: ClientLocationClass[]
  [key: string]: any
}

function buildClassTree(rawClasses: ClientClass[]): ClientClass[] {
  const childByParentId = _.groupBy(
    rawClasses.filter((item) => !!item.parent_class_id),
    'parent_class_id'
  )

  const parentClasses = rawClasses.filter((item) => !item.parent_class_id)
  const ordered: ClientClass[] = []

  parentClasses.forEach((parentClass) => {
    ordered.push({ ...parentClass, _type: 'parent' })

    const children = childByParentId[parentClass.id] || []
    children.forEach((childClass) => {
      ordered.push({ ...childClass, _type: 'child' })
    })
  })

  // Keep children whose parent is missing/inactive in payload so they remain visible and editable.
  const parentIds = new Set(parentClasses.map((item) => item.id))
  const orphanChildren = rawClasses.filter(
    (item) => !!item.parent_class_id && !parentIds.has(item.parent_class_id as number)
  )
  orphanChildren.forEach((childClass) => {
    ordered.push({ ...childClass, _type: 'orphan_child' })
  })

  return ordered
}

function ClientClasses(props: Props) {
  const isV3Variant = props?.visualVariant === 'v3'
  const styles = getProfileCommonJss(props?.visualVariant)

  const [locations, setLocations] = useState<ClientLocation[]>([])
  const [classes, setClasses] = useState<ClientClass[]>([])
  const [selectedClasses, setSelectedClasses] = useState<ClientClass[]>([])
  const [selectedLocations, setSelectedLocations] = useState<ClientLocation[]>([])
  const [multiMode, setMultiMode] = useState(false)
  const [labelData, setLabelData] = useState<any>()
  const [categories, setCategories] = useState<any>()
  const [masterCategories, setMasterCategories] = useState<any[]>([])
  const [isAdmin, setIsAdmin] = useState(false)

  const tipContext = TipsContext.useMessage()
  const currentClientContext = CurrentClientContext.useClient() as CurrentClientContextValue
  const auth = AuthProvider.useAuth() as AuthContextValue
  const clientId = currentClientContext.client?.Id

  const selectedLocationClass = useMemo(() => {
    const selectedLocation = selectedLocations[0]
    const selectedClass = selectedClasses[0]

    if (!selectedLocation || !selectedClass) {
      return null
    }

    return (
      selectedLocation.class_links?.find(
        (link) => Number(link.clientclass_id) === Number(selectedClass.id)
      ) || null
    )
  }, [selectedLocations, selectedClasses])

  useEffect(() => {
    document.title = 'Retail ORBIT: Client Classes'
  }, [])

  useEffect(() => {
    setIsAdmin(!!auth?.checkPermission?.('role-create'))
  }, [auth])

  useEffect(() => {
    if (clientId) {
      void getClientClasses()
      void getClientLocations()
      void loadLabels()
      void loadCategories()
      void loadMasterCategories()
    }
  }, [clientId])

  async function loadLabels() {
    const data = await LabelService.getLabels()
    setLabelData(data)
  }

  async function loadCategories() {
    if (!clientId) {
      return
    }
    const data = await ClientService.getClientCategories(clientId)
    setCategories(data)
  }

  async function loadMasterCategories() {
    const data = await ClientService.getMasterCategories()
    setMasterCategories(data)
  }

  async function getClientClasses() {
    if (!clientId) {
      return
    }

    const result: ClientClass[] = await ClientService.getClientClasses(clientId, {
      include: [
        'parentClass',
        'locationLinks',
        'labels',
        'categories',
        'warehouseLocation',
        'childClasses',
        'masterClasses',
      ],
    })

    const data = buildClassTree(result || [])
    setClasses(data)
  }

  async function getClientLocations() {
    if (!clientId) {
      return
    }

    const result: ClientLocation[] = await ClientService.getClientLocations(clientId, {
      include: [
        'classLinks.labels',
        'classLinks.class',
        'classLinks.masterClasses.masterClass',
        'classLinks.masterCategories.masterCategory',
        'classLinks.children',
        'classLinks.categories',
        'classLinks.parentClass',
        'pos',
        'labels',
      ],
    })
    setLocations(result || [])
  }

  async function refreshClassAndLocationLists(focusClassId?: number) {
    if (!clientId) {
      return
    }

    const [rawClasses, rawLocations] = await Promise.all([
      ClientService.getClientClasses(clientId, {
        include: [
          'parentClass',
          'locationLinks',
          'labels',
          'categories',
          'warehouseLocation',
          'childClasses',
          'masterClasses',
        ],
      }),
      ClientService.getClientLocations(clientId, {
        include: [
          'classLinks.labels',
          'classLinks.class',
          'classLinks.masterClasses.masterClass',
          'classLinks.masterCategories.masterCategory',
          'classLinks.children',
          'classLinks.categories',
          'classLinks.parentClass',
          'pos',
          'labels',
        ],
      }),
    ])

    const nextClasses = buildClassTree((rawClasses || []) as ClientClass[])
    const nextLocations = (rawLocations || []) as ClientLocation[]

    setClasses(nextClasses)
    setLocations(nextLocations)

    setSelectedClasses((prev) => {
      if (focusClassId) {
        const focusedClass = nextClasses.find((item) => Number(item.id) === Number(focusClassId))
        return focusedClass ? [focusedClass] : []
      }

      if (!prev.length) {
        return prev
      }

      const selectedIds = new Set(prev.map((item) => Number(item.id)))
      return nextClasses.filter((item) => selectedIds.has(Number(item.id)))
    })

    setSelectedLocations((prev) => {
      if (!prev.length) {
        return prev
      }

      const selectedLocationIds = new Set(
        prev.map((location) => Number(location.client_location_id ?? location.id))
      )

      return nextLocations.filter((location) => {
        return selectedLocationIds.has(Number(location.client_location_id ?? location.id))
      })
    })
  }

  function classChoose(items: ClientClass[]) {
    setSelectedClasses(items || [])
  }

  function locationChoose(items: ClientLocation[]) {
    setSelectedLocations(items || [])
  }

  function handleDeleteClass(deletedClass: ClientClass) {
    const nextClasses = classes.map((clientClass) =>
      Number(clientClass.id) === Number(deletedClass.id) ? { ...clientClass, ...deletedClass } : clientClass
    )
    setClasses(nextClasses)

    const nextSelectedClasses = selectedClasses.map((clientClass) =>
      Number(clientClass.id) === Number(deletedClass.id) ? { ...clientClass, ...deletedClass } : clientClass
    )
    setSelectedClasses(nextSelectedClasses)

    const nextLocations = locations.map((loc) => ({
      ...loc,
      class_links: (loc.class_links || []).map((locationClassLink) => {
        if (Number(locationClassLink.clientclass_id) === Number(deletedClass.id)) {
          return {
            ...locationClassLink,
            isActive: deletedClass.isActive,
            deleted_at: deletedClass.deleted_at,
          }
        }
        return locationClassLink
      }),
    }))
    setLocations(nextLocations)

    const nextSelectedLocations = selectedLocations.map((loc) => ({
      ...loc,
      class_links: (loc.class_links || []).map((locationClassLink) => {
        if (Number(locationClassLink.clientclass_id) === Number(deletedClass.id)) {
          return {
            ...locationClassLink,
            isActive: deletedClass.isActive,
            deleted_at: deletedClass.deleted_at,
          }
        }
        return locationClassLink
      }),
    }))
    setSelectedLocations(nextSelectedLocations)
  }

  function handleSingleClassUpdate(updatedClass: ClientClass) {
    const nextSelectedClasses = selectedClasses.map((item) =>
      Number(item.id) === Number(updatedClass.id) ? updatedClass : item
    )
    const nextAllClasses = classes.map((item) =>
      Number(item.id) === Number(updatedClass.id) ? updatedClass : item
    )

    setSelectedClasses(nextSelectedClasses)
    setClasses(nextAllClasses)
    void refreshClassAndLocationLists(Number(updatedClass.id))
  }

  function onBulkDeleted(deletedClasses: ClientClass[]) {
    const deletedIds = new Set((deletedClasses || []).map((item) => Number(item.id)))

    setClasses((prev) => prev.filter((item) => !deletedIds.has(Number(item.id))))
    setSelectedClasses((prev) => prev.filter((item) => !deletedIds.has(Number(item.id))))

    void getClientLocations()
    void getClientClasses()
  }

  const handleClientLocationClassChange = (updatedClientLocationClass: ClientLocationClass | any) => {
    if (!updatedClientLocationClass) {
      return
    }

    const patchLocation = (location: ClientLocation): ClientLocation => ({
      ...location,
      class_links: (location.class_links || []).map((locationClassLink) => {
        if (
          Number(locationClassLink.locationclassid) ===
          Number(updatedClientLocationClass.locationclassid)
        ) {
          return updatedClientLocationClass
        }
        return locationClassLink
      }),
    })

    setLocations((prev) => prev.map(patchLocation))
    setSelectedLocations((prev) => prev.map(patchLocation))

    setSelectedClasses((prev) =>
      prev.map((clientClass) => {
        if (Number(clientClass.id) !== Number(updatedClientLocationClass.clientclass_id)) {
          return clientClass
        }

        const updatedLinks = (clientClass.location_links || []).map((locationLink: any) => {
          if (
            Number(locationLink.locationclassid) ===
            Number(updatedClientLocationClass.locationclassid)
          ) {
            return updatedClientLocationClass
          }
          return locationLink
        })

        return {
          ...clientClass,
          location_links: updatedLinks,
        }
      })
    )
  }

  function createClass(item: ClientClass, removeTmp: string | false = false) {
    let nextClasses = [...classes]

    if (removeTmp) {
      nextClasses = nextClasses.filter((existingClass) => {
        if (existingClass.id === -1 && removeTmp === existingClass.name) {
          return false
        }
        return true
      })
    }

    const nextItem: ClientClass = {
      ...item,
      client_id: clientId,
    }

    nextClasses.unshift(nextItem)
    setClasses(nextClasses)
    setSelectedClasses([nextItem])

    void getClientLocations()
  }

  const updateLocations = (updatedLocations: ClientLocation[]) => {
    setLocations(updatedLocations || [])
  }

  return (
    <Box sx={{ py: 2 }} className={isV3Variant ? 'v3-profile-collection-page' : undefined}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={7}>
          <Box sx={styles.title} className={isV3Variant ? 'v3-profile-collection-title' : undefined}>
            CLASSES
          </Box>
          <Box sx={styles.dataGrid} className={isV3Variant ? 'v3-profile-collection-grid' : undefined}>
            <ClientProfileClassList
              classes={classes}
              currentLocations={selectedLocations}
              onClassChange={classChoose}
              onMultiModeChange={(e: any) => setMultiMode(e.target.checked)}
              createClass={createClass}
              multiMode={multiMode}
              isAdmin={isAdmin}
              visualVariant={props?.visualVariant}
            />
            <ClientProfileLocationList
              locations={locations}
              currentClasses={selectedClasses}
              onLocationChange={locationChoose}
              onCurrentClassChange={handleSingleClassUpdate}
              multiMode={multiMode}
              assignAvailable={true}
              updateLocations={updateLocations}
              isAdmin={isAdmin}
              visualVariant={props?.visualVariant}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={5}>
          <Box sx={styles.title} className={isV3Variant ? 'v3-profile-collection-title' : undefined}>
            {' '}
          </Box>

          {!multiMode && selectedClasses.length > 0 && selectedClasses[0].id === -1 && (
            <ClientClassCreate
              classItem={selectedClasses[0]}
              allClasses={classes}
              locations={locations}
              onSave={createClass}
              masterCategories={masterCategories}
              labelData={labelData}
              categories={categories}
              tip_context={tipContext}
            />
          )}

          {!multiMode && selectedClasses.length > 0 && selectedClasses[0].id !== -1 && (
            <ClientClassEdit
              clientClass={selectedClasses[0]}
              locations={locations}
              onSave={handleSingleClassUpdate}
              allClasses={classes}
              masterCategories={masterCategories}
              labelData={labelData}
              categories={categories}
              isAdmin={isAdmin}
              onDeleteChange={handleDeleteClass}
              tip_context={tipContext}
              visualVariant={props?.visualVariant}
            />
          )}

          {isAdmin && multiMode && selectedClasses.length > 0 && (
            <ClassBulkEdit classes={selectedClasses} locations={locations} onDeleted={onBulkDeleted} />
          )}

          {!multiMode &&
            selectedLocations.length > 0 &&
            !!selectedLocationClass &&
            !selectedLocations[0]?.drop_date && (
              <ClientLocationClassLinkEdit
                location={selectedLocations[0]}
                clientClass={selectedLocationClass}
                categories={categories}
                labels={labelData}
                locationClasses={selectedLocations[0].class_links}
                onSave={handleClientLocationClassChange}
                isAdmin={isAdmin}
                visualVariant={props?.visualVariant}
              />
            )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default ClientClasses
